import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonChatMainContainer from "./CommonChatMainContainer";
import CommonChatMessageInput from "./CommonChatMessageInput";
import { GLOBAL_REPORT_ASK_QUESTION_OPEN } from "../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../utils/constants";
import { BOX_SHADOW_BUTTON, INPUT_COLOR_BORDER } from "../../utils/colors";

const COLLAPSE_TIMEOUT = { enter: 200, exit: 150 };

const INPUT_SX = {
  boxShadow: "none",
  "& .MuiInputBase-root": {
    border: "none !important",
    boxShadow: "none !important",
    backgroundColor: "transparent !important",
  },
  "& .MuiInputBase-input, & .MuiFilledInput-input": {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "12px",
    marginY: 1,
    borderRadius: BORDER_RADIUS,
  },
};

const InlineExpandableChat = ({
  isOpen: isOpenProp,
  onOpenChange: onOpenChangeProp,
  disabled = false,
  agentFlow = "globalReport",
  globalReportContext,
  placeholder = GLOBAL_REPORT_ASK_QUESTION_OPEN,
  statisticsView = true,
  stayExpandedWhenHasMessages = true,
  sx,
  inputBarSx,
}) => {
  const isControlled =
    typeof isOpenProp === "boolean" && typeof onOpenChangeProp === "function";
  const [internalOpen, setInternalOpen] = useState(false);
  const [messageCount, setMessageCount] = useState(0);

  const isOpen = isControlled ? isOpenProp : internalOpen;
  const setOpen = isControlled ? onOpenChangeProp : setInternalOpen;

  const hasMessages = messageCount > 0;
  const isExpanded = stayExpandedWhenHasMessages && hasMessages ? true : isOpen;

  const containerRef = useRef(null);
  const messagesPanelRef = useRef(null);
  const chatSendHandlerRef = useRef(null);

  useEffect(() => {
    if (!isExpanded || (stayExpandedWhenHasMessages && hasMessages)) {
      return;
    }

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [hasMessages, isExpanded, setOpen, stayExpandedWhenHasMessages]);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    const panel = messagesPanelRef.current;
    if (!panel) {
      return;
    }

    const handleWheel = (event) => {
      const scrollEl = panel.querySelector(".scrollbar-container");
      if (!scrollEl) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = scrollEl;
      if (scrollHeight <= clientHeight) {
        return;
      }

      const delta = event.deltaY;
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

      if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
        return;
      }

      scrollEl.scrollTop += delta;
      event.preventDefault();
    };

    panel.addEventListener("wheel", handleWheel, {
      passive: false,
      capture: true,
    });
    return () =>
      panel.removeEventListener("wheel", handleWheel, { capture: true });
  }, [isExpanded]);

  const handleSendMessage = async (text) => {
    const trimmed = typeof text === "string" ? text.trim() : "";
    if (!trimmed) {
      return;
    }

    if (!isExpanded) {
      setOpen(true);
    }

    return chatSendHandlerRef.current?.(text);
  };

  const handleCollapse = (event) => {
    event.stopPropagation();
    setOpen(false);
  };

  const showCollapseButton =
    isExpanded && !(stayExpandedWhenHasMessages && hasMessages);

  const pillRadius = "9999px";

  return (
    <Box
      ref={containerRef}
      className="inline-expandable-chat"
      sx={{
        width: "100%",
        position: "relative",
        zIndex: 0,
        isolation: "isolate",
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        boxShadow: BOX_SHADOW_BUTTON,
        borderRadius: isExpanded ? `${BORDER_RADIUS * 2}px` : pillRadius,
        overflow: "hidden",
        ...sx,
      }}
    >
      <Collapse in={isExpanded} timeout={COLLAPSE_TIMEOUT}>
        <Box
          ref={messagesPanelRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 280,
            maxHeight: 400,
            height: 360,
            overflow: "hidden",
            borderBottom: inputBarSx
              ? "none"
              : `1px solid ${INPUT_COLOR_BORDER}`,
          }}
        >
          <CommonChatMainContainer
            statisticsView={statisticsView}
            embedded
            hideLauncher
            showOnlyMessages
            controlledOpen={true}
            showPreviewOptimization={false}
            showHeader={false}
            showMinimize={false}
            messageVariant="inline"
            exposeSendMessage={(handler) => {
              chatSendHandlerRef.current = handler;
            }}
            exposeMessageCount={setMessageCount}
            agentFlow={agentFlow}
            globalReportContext={globalReportContext}
            startMode="messageInput"
            placeholderInput={placeholder}
            headerTitle={placeholder}
            sx={{
              position: "relative",
              bottom: "auto",
              right: "auto",
              width: "100%",
              flex: 1,
              minHeight: 0,
              height: "100%",
              boxShadow: 0,
              border: 0,
              display: "flex",
              flexDirection: "column",
            }}
            contentSx={{
              flex: 1,
              minHeight: 0,
              maxHeight: "100%",
              width: "100%",
              height: "100%",
            }}
          />
        </Box>
      </Collapse>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.5,
          minHeight: 56,
          ...inputBarSx,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CommonChatMessageInput
            multiline={false}
            disabled={disabled}
            onSendMessage={handleSendMessage}
            placeholderInput={placeholder}
            sx={INPUT_SX}
            messageVariant="inline"
          />
        </Box>

        {showCollapseButton && (
          <IconButton
            size="small"
            onClick={handleCollapse}
            sx={{ color: "text.secondary", flexShrink: 0, mr: 0.5 }}
            aria-label="collapse chat"
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default InlineExpandableChat;
